import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FlexSpacer } from 'ui/flexSpacer';
import { BoldText, H3 } from 'ui/theme/typography/components';
import { AddNew16 } from 'ui/icons/aurora/16px';
import { Spinner } from 'ui/spinner';
import { Table } from 'ui/table';
import { sortRows } from 'ui/table/utils';
import { TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { PrimaryButton, BUTTON_VARIANTS } from 'ui/button';
import { ConfirmModal } from 'ui/modal';
import { DESCENDING } from 'constants/sortDirections';
import {
  getIsLoading,
  selectDefaultTrackingDomain,
  selectProjectHasSPFRecords,
} from '../selectors';
import { deleteSendingDomainRequest, editSendingDomainRequest } from '../actions';
import {
  CREATE_SENDING_DOMAIN,
  DELETE_SENDING_DOMAIN,
  SET_TRACKING_DOMAIN,
  VERIFY_SENDING_DOMAIN,
} from '../constants';
import { configureSendingDomainsTableColumns } from './SendingDomainsTableColumns';
import { CreateSendingDomainModal } from './CreateSendingDomainModal';
import { SetTrackingDomainModal } from './SetTrackingDomainModal';
import { EmptyStateContainer } from './MailDomains.styled';
// import { SendingDomain } from '../types';

// type Props = {
//   sendingDomains: Array<SendingDomain>,
//   projectHasTrackingDomains: boolean,
// };

export const SendingDomainsTable = ({ sendingDomains = [], projectHasTrackingDomains = false }) => {
  const [selectedSendingDomain, setSelectedSendingDomain] = useState({});
  const [sortedBy, setSortedBy] = useState('domain');
  const [modalState, setModalState] = useState('');
  const closeModal = () => setModalState('');
  const dispatch = useDispatch();

  const deleteSendingDomain = () => {
    dispatch(deleteSendingDomainRequest(selectedSendingDomain.id));
    closeModal();
  };

  const verifySendingDomain = (isVerified /*: boolean */) => {
    dispatch(editSendingDomainRequest({ ...selectedSendingDomain, isVerified }));
    closeModal();
  };

  const createSendingDomainModalIsOpen = modalState === CREATE_SENDING_DOMAIN;
  const deleteSendingDomainModalIsOpen = modalState === DELETE_SENDING_DOMAIN;
  const setTrackingDomainModalIsOpen = modalState === SET_TRACKING_DOMAIN;
  const verifyDomainModalIsOpen = modalState === VERIFY_SENDING_DOMAIN;

  const isLoading = useSelector(getIsLoading);
  const [currentSortDirection, setSortDirection] = useState(DESCENDING);
  const sortedRows = sortRows(sendingDomains, sortedBy, currentSortDirection);

  const onOpen = (domain, modalType) => {
    setSelectedSendingDomain(domain);
    setModalState(modalType);
  };

  const defaultTrackingDomain = useSelector(selectDefaultTrackingDomain);
  const showSPFColumn = useSelector(selectProjectHasSPFRecords);
  const sendingDomainsTableColumns = configureSendingDomainsTableColumns({
    onSetTrackingDomain: (domain) => onOpen(domain, SET_TRACKING_DOMAIN),
    onVerify: (domain) => onOpen(domain, VERIFY_SENDING_DOMAIN),
    onDelete: (domain) => onOpen(domain, DELETE_SENDING_DOMAIN),
    defaultTrackingDomain,
    projectHasTrackingDomains,
    showSPFColumn,
  });

  return (
    <>
      {isLoading ? (
        <EmptyStateContainer data-test="emptyState">
          <Spinner />
        </EmptyStateContainer>
      ) : (
        <>
          <FlexSpacer justifyContent="space-between">
            <H3>Sending domains</H3>
            <PrimaryButton
              data-test="mailDomains-create-sending-domain-button"
              onClick={() => setModalState(CREATE_SENDING_DOMAIN)}
            >
              <AddNew16 />
              New sending domain
            </PrimaryButton>
          </FlexSpacer>
          <Table data-test="sending-domains-table">
            <TableHeader
              columns={sendingDomainsTableColumns}
              setSortedBy={setSortedBy}
              setSortDirection={setSortDirection}
              sortDirection={currentSortDirection}
              sortedBy={sortedBy}
            />
            <TableRows idKey="domain" columns={sendingDomainsTableColumns} rows={sortedRows} />
          </Table>
          {!sortedRows.length && (
            <FlexSpacer padding="cardPadding" justifyContent="center">
              No results
            </FlexSpacer>
          )}
        </>
      )}
      {createSendingDomainModalIsOpen && <CreateSendingDomainModal onClose={closeModal} />}
      {verifyDomainModalIsOpen && (
        <ConfirmModal
          isModalOpen={verifyDomainModalIsOpen}
          onClose={closeModal}
          cancelLabel="Cancel"
          id="verify-sending-domain-modal"
          onConfirm={() => verifySendingDomain(!selectedSendingDomain.isVerified)}
          confirmButtonVariant={
            selectedSendingDomain.isVerified ? BUTTON_VARIANTS.DESTRUCTIVE : BUTTON_VARIANTS.PRIMARY
          }
          confirmButtonContent={selectedSendingDomain.isVerified ? 'Unverify' : 'Verify'}
          headerContent={`${
            selectedSendingDomain.isVerified ? 'Unverify' : 'Verify'
          } sending domain`}
          bodyContent={
            <>
              Are you sure you would like to{' '}
              {selectedSendingDomain.isVerified ? 'unverify' : 'verify'} this domain?
            </>
          }
        />
      )}
      {deleteSendingDomainModalIsOpen && (
        <ConfirmModal
          isModalOpen={deleteSendingDomainModalIsOpen}
          onClose={closeModal}
          cancelLabel="Cancel"
          id="delete-sending-domain-modal"
          onConfirm={deleteSendingDomain}
          confirmButtonVariant={BUTTON_VARIANTS.DESTRUCTIVE}
          confirmButtonContent="Delete"
          headerContent="Delete sending domain"
          bodyContent={
            <>
              Are you sure you would like to delete{' '}
              <BoldText>{selectedSendingDomain.domain}</BoldText>?
            </>
          }
        />
      )}
      {setTrackingDomainModalIsOpen && (
        <SetTrackingDomainModal sendingDomain={selectedSendingDomain} onClose={closeModal} />
      )}
    </>
  );
};
